// ── Share cards (17) ───────────────────────────────────────────────────────────
// One portrait card, 1080×1350, built on canvas and handed to the phone's share
// sheet as a PNG so it lands in iMessage / WhatsApp as a picture, not a link.
// Poster on top, the app's own type below: mono eyebrow, serif headline, a line,
// the Next/EP wordmark. The poster's palette tints the ground (same helper the
// deck uses), so every card is that show's colour.
import { loadImage, wrapText } from './shareCanvas'
import { paletteFromPoster } from './nowPlaying'

const W = 1080, H = 1350, PAD = 72
const poster500 = (p) => `/.netlify/functions/img-proxy?p=${encodeURIComponent(p)}&s=w500`

async function fontsReady() {
  try { await document.fonts?.load('500 72px Fraunces'); await document.fonts?.load('500 30px "JetBrains Mono"'); await document.fonts?.ready } catch {}
}

function fitText(ctx, text, maxW, sizes, family, weight = 500) {
  for (const s of sizes) { ctx.font = `${weight} ${s}px ${family}`; if (ctx.measureText(text).width <= maxW) return s }
  return sizes[sizes.length - 1]
}

// { show, eyebrow, headline, line, quote } → Blob (image/png)
// `quote` (the Final Cut card's take): an italic serif line under `line`, wrapped
// to two lines, in quotation marks — the one thing on the card in the member's
// own words.
export async function renderShareCard({ show, eyebrow = 'NextEp', headline, line, big, quote }) {
  await fontsReady()
  if (big != null) return renderBigCard({ eyebrow, headline, line, big })
  let img = null
  try { if (show?.poster_path) img = await loadImage(poster500(show.poster_path)) } catch {}
  let pal = null
  try { if (show?.poster_path) pal = await paletteFromPoster(show.poster_path) } catch {}

  const c = document.createElement('canvas'); c.width = W; c.height = H
  const ctx = c.getContext('2d')
  // Ground: the show's raised tint at the top fading to near-black.
  const g = ctx.createLinearGradient(0, 0, 0, H)
  g.addColorStop(0, pal?.raised || '#14141f'); g.addColorStop(0.55, pal?.bg || '#0b0b12'); g.addColorStop(1, '#07070f')
  ctx.fillStyle = g; ctx.fillRect(0, 0, W, H)

  // Poster, centred, 2:3, rounded, with a soft shadow.
  const pw = 520, ph = 780, px = (W - pw) / 2, py = PAD
  ctx.save()
  ctx.shadowColor = 'rgba(0,0,0,0.55)'; ctx.shadowBlur = 60; ctx.shadowOffsetY = 24
  ctx.fillStyle = '#0a0a14'; roundRect(ctx, px, py, pw, ph, 28); ctx.fill()
  ctx.restore()
  ctx.save(); roundRect(ctx, px, py, pw, ph, 28); ctx.clip()
  if (img) {
    const r = Math.max(pw / img.width, ph / img.height), dw = img.width * r, dh = img.height * r
    ctx.drawImage(img, px + (pw - dw) / 2, py + (ph - dh) / 2, dw, dh)
  } else { ctx.fillStyle = '#14141f'; ctx.fillRect(px, py, pw, ph) }
  ctx.restore()
  ctx.strokeStyle = 'rgba(255,255,255,0.12)'; ctx.lineWidth = 2; roundRect(ctx, px, py, pw, ph, 28); ctx.stroke()

  // Type block.
  let y = py + ph + 84
  ctx.textAlign = 'center'; ctx.textBaseline = 'alphabetic'
  ctx.fillStyle = '#f5c842'; ctx.font = '500 28px "JetBrains Mono", Menlo, monospace'
  try { ctx.letterSpacing = '0.14em' } catch {}
  ctx.fillText((eyebrow || '').toUpperCase(), W / 2, y)
  try { ctx.letterSpacing = '0px' } catch {}
  y += 74
  const hs = fitText(ctx, headline || show?.name || '', W - PAD * 2, [76, 68, 60, 52, 46], 'Fraunces, Georgia, serif')
  ctx.fillStyle = '#ffffff'; ctx.font = `500 ${hs}px Fraunces, Georgia, serif`
  ctx.fillText(headline || show?.name || '', W / 2, y)
  if (line) {
    y += 62
    const ls = fitText(ctx, line, W - PAD * 2, [34, 30, 27], 'Outfit, -apple-system, sans-serif', 400)
    ctx.fillStyle = 'rgba(231,231,255,0.78)'; ctx.font = `400 ${ls}px Outfit, -apple-system, sans-serif`
    ctx.fillText(line, W / 2, y)
  }
  if (quote) {
    y += 64
    ctx.fillStyle = 'rgba(255,255,255,0.9)'; ctx.font = 'italic 400 34px Fraunces, Georgia, serif'
    wrapText(ctx, `“${quote}”`, W / 2, y, W - PAD * 2 - 40, 44, 2, true)
  }
  // Wordmark, bottom.
  ctx.fillStyle = '#f5c842'
  ctx.font = 'italic 400 44px Fraunces, Georgia, serif'
  const nw = ctx.measureText('Next').width
  ctx.font = '500 26px "JetBrains Mono", Menlo, monospace'
  const ew = ctx.measureText('EP').width
  const x0 = W / 2 - (nw + ew + 6) / 2
  ctx.textAlign = 'left'
  ctx.font = 'italic 400 44px Fraunces, Georgia, serif'; ctx.fillText('Next', x0, H - PAD)
  ctx.font = '500 26px "JetBrains Mono", Menlo, monospace'; ctx.fillText('EP', x0 + nw + 6, H - PAD)
  ctx.textAlign = 'center'
  ctx.fillStyle = 'rgba(231,231,255,0.45)'; ctx.font = '400 22px Outfit, -apple-system, sans-serif'
  ctx.fillText('app.nextep.show', W / 2, H - PAD + 40)

  return new Promise((res, rej) => c.toBlob(b => b ? res(b) : rej(new Error('toBlob failed')), 'image/png'))
}

// A number card: no poster, one huge serif figure (the ladder, the year, a streak).
async function renderBigCard({ eyebrow, headline, line, big }) {
  const c = document.createElement('canvas'); c.width = W; c.height = H
  const ctx = c.getContext('2d')
  const g = ctx.createLinearGradient(0, 0, 0, H); g.addColorStop(0, '#1a1508'); g.addColorStop(0.6, '#0b0b12'); g.addColorStop(1, '#07070f')
  ctx.fillStyle = g; ctx.fillRect(0, 0, W, H)
  ctx.textAlign = 'center'; ctx.textBaseline = 'alphabetic'
  ctx.fillStyle = '#f5c842'; ctx.font = '500 30px "JetBrains Mono", Menlo, monospace'
  try { ctx.letterSpacing = '0.14em' } catch {}
  ctx.fillText((eyebrow || '').toUpperCase(), W / 2, 420)
  try { ctx.letterSpacing = '0px' } catch {}
  const bs = fitText(ctx, String(big), W - PAD * 2, [300, 260, 220, 180, 140], 'Fraunces, Georgia, serif')
  ctx.fillStyle = '#ffffff'; ctx.font = `500 ${bs}px Fraunces, Georgia, serif`
  ctx.fillText(String(big), W / 2, 700)
  if (headline) { const hs = fitText(ctx, headline, W - PAD * 2, [64, 56, 48], 'Fraunces, Georgia, serif'); ctx.font = `500 ${hs}px Fraunces, Georgia, serif`; ctx.fillText(headline, W / 2, 790) }
  if (line) { const ls = fitText(ctx, line, W - PAD * 2, [34, 30, 27], 'Outfit, -apple-system, sans-serif', 400); ctx.fillStyle = 'rgba(231,231,255,0.78)'; ctx.font = `400 ${ls}px Outfit, -apple-system, sans-serif`; ctx.fillText(line, W / 2, 850) }
  ctx.fillStyle = '#f5c842'
  ctx.font = 'italic 400 44px Fraunces, Georgia, serif'; const nw = ctx.measureText('Next').width
  ctx.font = '500 26px "JetBrains Mono", Menlo, monospace'; const ew = ctx.measureText('EP').width
  const x0 = W / 2 - (nw + ew + 6) / 2; ctx.textAlign = 'left'
  ctx.font = 'italic 400 44px Fraunces, Georgia, serif'; ctx.fillText('Next', x0, H - PAD)
  ctx.font = '500 26px "JetBrains Mono", Menlo, monospace'; ctx.fillText('EP', x0 + nw + 6, H - PAD)
  ctx.textAlign = 'center'; ctx.fillStyle = 'rgba(231,231,255,0.45)'; ctx.font = '400 22px Outfit, -apple-system, sans-serif'
  ctx.fillText('app.nextep.show', W / 2, H - PAD + 40)
  return new Promise((res, rej) => c.toBlob(b => b ? res(b) : rej(new Error('toBlob failed')), 'image/png'))
}

function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath(); ctx.moveTo(x + r, y); ctx.lineTo(x + w - r, y); ctx.quadraticCurveTo(x + w, y, x + w, y + r)
  ctx.lineTo(x + w, y + h - r); ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h); ctx.lineTo(x + r, y + h)
  ctx.quadraticCurveTo(x, y + h, x, y + h - r); ctx.lineTo(x, y + r); ctx.quadraticCurveTo(x, y, x + r, y); ctx.closePath()
}

// Render and hand to the share sheet. Returns 'shared' | 'saved' | 'failed'.
// Files only: a text/title pre-fills the SMS body as literal text on iOS.
export async function shareCard(opts) {
  try {
    const blob = await renderShareCard(opts)
    const file = new File([blob], 'nextep.png', { type: 'image/png' })
    // The image alone has nowhere to tap; a tagged link rides along so a shared card is a door.
    if (navigator.canShare?.({ files: [file] })) { await navigator.share({ files: [file], title: 'NextEp', text: 'Made with NextEp · https://app.nextep.show/start?ref=share-card' }); return 'shared' }
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a'); a.href = url; a.download = 'nextep.png'; a.click()
    setTimeout(() => URL.revokeObjectURL(url), 2000)
    return 'saved'
  } catch (e) {
    if (e?.name === 'AbortError') return 'cancelled'
    return 'failed'
  }
}
